// ============================================================================
// ÄNDERUNGSPROTOKOLL - PlatformSection.js
// ============================================================================
//
// DATUM: 25.11.2025
//
// ZWECK DER ÄNDERUNG:
// Die Abstände (margin-top und padding-top) der PlatformSection wurden reduziert,
// da die darüberliegende StatsSection temporär ausgeblendet wurde. Ohne diese
// Anpassung wäre ein großer Leerraum zwischen HeroSection und PlatformSection
// entstanden.
//
// AUSGANGSPUNKT (VORHER):
// - className enthielt: mt-32 (margin-top: 8rem)
// - className enthielt: min-[1800px]:mt-[10rem]
// - style enthielt: paddingTop: '8rem'
//
// ENDPUNKT (NACHHER):
// - className enthält jetzt: mt-8 (margin-top: 2rem)
// - className enthält jetzt: min-[1800px]:mt-[4rem]
// - style enthält jetzt: paddingTop: '4rem'
//
// VERKNÜPFUNG:
// Diese Änderung hängt zusammen mit StatsSection.js - dort wurde die gesamte
// Komponente auskommentiert (return null), weshalb hier die Abstände angepasst
// werden mussten. Wenn StatsSection wieder aktiviert wird, sollten diese
// Werte zurückgesetzt werden auf die ursprünglichen Werte (mt-32, mt-[10rem],
// paddingTop: '8rem').
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import FadeInSection from './FadeInSection';
import ContactFormModal from './ContactFormModal';
import { useTranslation } from '../i18n/useTranslation';
import { trackContactClick } from '../track';
import { isMobile, shouldPreloadAggressively } from '../utils/deviceDetection';
import { useInViewport } from '../hooks/useLazyLoad';
import { preloader } from '../utils/ResourcePreloader';

const PlatformSection = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('plattform');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // High-End-Modus: Alle Videos gleichzeitig
  const [canRunAllVideos, setCanRunAllVideos] = useState(false);

  // Preloading-State: Sind Videos vom Preloader geladen?
  const [videosPreloaded, setVideosPreloaded] = useState(false);

  // Loading-State pro Video
  const [loaded, setLoaded] = useState({});

  // Refs für die Videos
  const videoRefs = useRef({});
  const singleVideoRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Viewport-Check für die gesamte Section
  const { elementRef: sectionRef, isInViewport } = useInViewport({
    threshold: 0.2,
    rootMargin: '50px',
    freezeOnceVisible: false
  });

  // Handler für Tab-Wechsel mit Video-Position-Fixierung (nur Mobile)
  const handleTabChange = (newTabId) => {
    const mobile = isMobile();

    if (!mobile || !videoContainerRef.current) {
      // Desktop: kein Fix nötig
      setActiveTab(newTabId);
      return;
    }

    // Mobile: Vertikale Position des Videos relativ zum Viewport merken
    const videoRect = videoContainerRef.current.getBoundingClientRect();
    const videoTopBeforeChange = videoRect.top;

    // Tab wechseln
    setActiveTab(newTabId);

    // Nach dem Render: Scroll so anpassen, dass Video an gleicher Position bleibt
    requestAnimationFrame(() => {
      const videoRectAfter = videoContainerRef.current.getBoundingClientRect();
      const videoTopAfterChange = videoRectAfter.top;

      // Differenz berechnen
      const diff = videoTopAfterChange - videoTopBeforeChange;

      // Scroll anpassen
      if (diff !== 0) {
        window.scrollBy(0, diff);
      }
    });
  };

  // Entscheide einmal: Multi-Video oder Single-Video Modus
  useEffect(() => {
    try {
      const mobile = isMobile();
      const saveData = navigator.connection?.saveData || false;
      const connectionType = navigator.connection?.effectiveType || '4g';

      // DEBUG: Zeige Info in Console
      console.log('🎥 PlatformSection Video Mode:', {
        mobile,
        deviceMemory: navigator.deviceMemory || 'unknown',
        connection: connectionType,
        saveData: saveData
      });

      // Videos sind klein (600-700KB), also Multi-Video IMMER außer:
      // - Save-Data Mode aktiv
      // - Sehr schlechte Verbindung (2G/slow-2G)
      const verySlowConnection = /^(slow-2g|2g)$/.test(connectionType);

      if (saveData || verySlowConnection) {
        setCanRunAllVideos(false);
        console.log('⚠️ Using Fallback Mode (save-data or slow connection)');
      } else {
        setCanRunAllVideos(true);
        console.log('✅ Using Multi-Video Mode (all 3 videos, instant switch)');
      }
    } catch (err) {
      // Bei Fehler: Multi-Video (optimistisch)
      setCanRunAllVideos(true);
      console.error('❌ Video mode detection failed, using Multi-Video:', err);
    }
  }, []);

  // Warte auf Preloader für die 3 Videos (nur High-End)
  useEffect(() => {
    if (!canRunAllVideos) {
      setVideosPreloaded(true); // Fallback Mode braucht kein Preload-Wait
      return;
    }

    const videoUrls = [
      '/videos/Platform.mp4',
      '/videos/Angebotserstellung.mp4',
      '/videos/Umlauf.mp4'
    ];

    // Check alle 100ms ob Videos preloaded sind
    const checkInterval = setInterval(() => {
      const allLoaded = videoUrls.every(url => preloader.isLoaded(url));
      if (allLoaded) {
        setVideosPreloaded(true);
        // Markiere ALLE Videos als geladen (verhindert weißen Flash)
        setLoaded({
          'plattform': true,
          'angebotsplanung': true,
          'umlauf-dienstplanung': true
        });
        clearInterval(checkInterval);
      }
    }, 100);

    // Timeout nach 5 Sekunden: zeige Videos auch wenn nicht preloaded
    const timeout = setTimeout(() => {
      setVideosPreloaded(true);
      // Markiere trotzdem alle als geladen
      setLoaded({
        'plattform': true,
        'angebotsplanung': true,
        'umlauf-dienstplanung': true
      });
      clearInterval(checkInterval);
    }, 5000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [canRunAllVideos]);

  const tabs = [
    {
      id: 'plattform',
      label: t('platform.tabs.plattform.label'),
      title: t('platform.tabs.plattform.title'),
      description: t('platform.tabs.plattform.description'),
      video: '/videos/Platform.mp4'
    },
    {
      id: 'angebotsplanung',
      label: t('platform.tabs.angebotsplanung.label'),
      title: t('platform.tabs.angebotsplanung.title'),
      description: t('platform.tabs.angebotsplanung.description'),
      video: '/videos/Angebotserstellung.mp4'
    },
    {
      id: 'umlauf-dienstplanung',
      label: t('platform.tabs.umlaufDienstplanung.label'),
      title: t('platform.tabs.umlaufDienstplanung.title'),
      description: t('platform.tabs.umlaufDienstplanung.description'),
      video: '/videos/Umlauf.mp4'
    }
  ];

  const activeContent = tabs.find(tab => tab.id === activeTab) || tabs[0];

  const handleVideoLoaded = (id) => {
    setLoaded(prev => ({ ...prev, [id]: true }));
  };

  /**
   * HIGH-END MODE: Alle Videos im DOM, play/pause basierend auf Tab + Viewport
   */
  useEffect(() => {
    if (!canRunAllVideos) return;

    const refs = videoRefs.current;
    if (!refs) return;

    Object.entries(refs).forEach(([id, el]) => {
      if (!el) return;

      const shouldPlay = isInViewport && id === activeTab;

      if (shouldPlay) {
        el.play().catch(() => {});
      } else {
        el.pause();
      }
    });
  }, [activeTab, isInViewport, canRunAllVideos]);

  /**
   * FALLBACK MODE: Ein Video, load() + play() nur im Viewport
   */
  useEffect(() => {
    if (canRunAllVideos) return;

    const v = singleVideoRef.current;
    if (!v) return;

    if (!isInViewport) {
      v.pause();
      return;
    }

    // Im Viewport + Tab gewechselt -> Video neu laden
    v.load();

    const onCanPlay = () => {
      v.play().catch(() => {});
    };

    v.addEventListener('canplay', onCanPlay, { once: true });

    return () => {
      v.removeEventListener('canplay', onCanPlay);
    };
  }, [activeTab, activeContent.video, isInViewport, canRunAllVideos]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-visible
          mt-8
          min-[1440px]:pt-16
          min-[1600px]:pt-24
          min-[1680px]:pt-28
          min-[1800px]:mt-[4rem]
          min-[1920px]:pt-32
          min-[2048px]:pt-40
          min-[2560px]:pt-48"
      style={{
        paddingTop: '4rem',
        zIndex: 2
      }}
    >
      {/* Separater Background-Layer mit clipPath */}
      <div className="absolute inset-0 bg-brand-light-blue" style={{
        clipPath: 'ellipse(110% 100% at 50% 100%)',
        zIndex: -1
      }}></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 min-[1800px]:pt-[6rem] relative" style={{paddingTop: '0rem'}}>
        <FadeInSection animation="fade-in-up" threshold={0.2}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-dark-blue leading-tight text-center">
            {t('platform.mainTitle')}<br/>{t('platform.mainSubtitle')}
          </h2>
          <p className="text-center text-lg text-gray-600 mt-4">{t('platform.highlights')}</p>
        </FadeInSection>

        <FadeInSection animation="fade-in-up" threshold={0.1} delay={200}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-16">
            <div>
              <h3 className="text-3xl font-bold text-brand-dark-blue">{activeContent.title}</h3>
              <p className="mt-4 text-lg text-gray-600">
                {activeContent.description}
              </p>
              <button
                onClick={() => {
                  trackContactClick('platform-section');
                  setIsContactModalOpen(true);
                }}
                className="mt-8 inline-block gradient-button gradient-button-hover text-white font-bold text-lg rounded-full px-10 py-4 transition-transform transform hover:scale-105"
              >
                {t('platform.ctaButton')}
              </button>
            </div>

            {/* Video Container */}
            <div ref={videoContainerRef} className="rounded-2xl mx-auto overflow-hidden bg-white">
              <div className="w-full max-w-[600px] mx-auto relative">
                {!videosPreloaded ? (
                  // Zeige Skeleton während Videos preloaden
                  <div className="w-full bg-gray-100 animate-pulse rounded-lg" style={{ paddingBottom: '56.25%' }} />
                ) : canRunAllVideos ? (
                  <>
                    {/* HIGH-END: Alle Videos gleichzeitig, opacity-switch */}
                    {tabs.map((tab) => (
                      <video
                        key={tab.id}
                        ref={el => {
                          if (el) {
                            if (!videoRefs.current) videoRefs.current = {};
                            videoRefs.current[tab.id] = el;
                          }
                        }}
                        muted
                        playsInline
                        loop
                        preload="auto"
                        style={{
                          maxHeight: '350px',
                          width: '100%',
                          display: 'block',
                          position: activeTab === tab.id ? 'relative' : 'absolute',
                          top: 0,
                          left: 0,
                          opacity: activeTab === tab.id && loaded[tab.id] ? 1 : 0,
                          transition: 'opacity 0.4s ease-in-out',
                          pointerEvents: activeTab === tab.id ? 'auto' : 'none'
                        }}
                        onCanPlayThrough={() => handleVideoLoaded(tab.id)}
                      >
                        <source src={tab.video} type="video/mp4" />
                      </video>
                    ))}

                    {/* Skeleton während Laden */}
                    {!loaded[activeTab] && (
                      <div
                        className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg"
                        style={{ height: '350px' }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* FALLBACK: Ein Video für Mobile/Low-End */}
                    <video
                      ref={singleVideoRef}
                      muted
                      playsInline
                      loop
                      preload={isInViewport ? 'metadata' : 'none'}
                      style={{
                        maxHeight: '350px',
                        width: '100%',
                        display: 'block',
                        opacity: loaded[activeContent.id] ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out'
                      }}
                      onCanPlayThrough={() => handleVideoLoaded(activeContent.id)}
                    >
                      <source src={activeContent.video} type="video/mp4" />
                      Ihr Browser unterstützt das Video-Tag nicht.
                    </video>

                    {/* Skeleton während Laden */}
                    {!loaded[activeContent.id] && (
                      <div
                        className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg"
                        style={{ height: '350px' }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* Tab Navigation - Below Content */}
        <div className="flex flex-wrap justify-center gap-3 mt-16">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300 transform
                ${activeTab === tab.id
                  ? 'bg-brand-dark-blue text-white scale-110 -translate-y-1'
                  : 'bg-white/30 text-brand-dark-blue border-2 border-white/60 hover:border-brand-dark-blue/30 hover:bg-white/50'
                }
              `}
              style={{
                boxShadow: activeTab === tab.id
                  ? '0 4px 20px rgba(39, 16, 102, 0.4), 0 0 30px rgba(59, 130, 246, 0.3)'
                  : '0 0 20px rgba(59, 130, 246, 0.2), inset 0 0 10px rgba(59, 130, 246, 0.1)'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </section>
  );
};

export default PlatformSection;

// npx firebase deploy --only hosting
